/* eslint-disable */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { LicenseFormHeader } from './license-form/LicenseFormHeader';
import { LicenseFormFields } from './license-form/LicenseFormFields';
import { LicenseCategoryAccessSection } from './license-form/LicenseCategoryAccessSection';
import { LicenseProductAccessSection } from './license-form/LicenseProductAccessSection';
import {
  LICENSE_LIST_PATH,
  buildDefaultAccessMap,
  countCustomAccessByCategory,
  isAssignedProduct,
} from './license-form/licenseFormUtils';
import {
  addLicense,
  deleteLicense,
  editLicense,
  fetchLicenseDetail,
  fetchLicenseList,
  fetchLicenseMatrix,
  searchProductItems,
} from './license-form/licenseApi';
import './ProductCategoryLicenseForm.css';

function mapLicensesFromApi(list) {
  return (list || []).map((item) => ({
    id: item.license_id,
    name: item.license_name,
    description: item.license_desc || '',
  }));
}

function ProductCategoryLicenseForm({ mode = 'create', app_hostname, permitdata }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = mode === 'edit';
  const pageTitle = isEdit ? 'แก้ไขใบทะเบียน' : 'เพิ่มใบทะเบียนใหม่';
  const preselectLicenseIdRef = useRef(location.state?.licenseId || '');

  const [categories, setCategories] = useState([]);
  const [assignedProductDetails, setAssignedProductDetails] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [bootLoading, setBootLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedLicenseId, setSelectedLicenseId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [categoryAccess, setCategoryAccess] = useState({});
  const [productAccess, setProductAccess] = useState({});
  const categoryAccessRef = useRef(categoryAccess);
  const productAccessRef = useRef(productAccess);
  categoryAccessRef.current = categoryAccess;
  productAccessRef.current = productAccess;

  const showAccessSections = !isEdit || Boolean(selectedLicenseId);

  const categoryNameById = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[category.id] = category.name;
      return acc;
    }, {});
  }, [categories]);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      setBootLoading(true);
      try {
        const matrixData = await fetchLicenseMatrix({
          appHostname: app_hostname,
          token: permitdata?.token,
        });

        if (cancelled) return;

        if (matrixData.status !== 'OK') {
          message.error(matrixData.message || 'โหลดหมวดหมู่ไม่สำเร็จ');
          return;
        }

        const nextCategories = (matrixData.rows || []).map((row) => ({
          id: row.id,
          name: row.name,
        }));
        setCategories(nextCategories);

        if (!isEdit) {
          setCategoryAccess(buildDefaultAccessMap(nextCategories, 'buy-sell'));
        }

        if (isEdit) {
          const listData = await fetchLicenseList({
            appHostname: app_hostname,
            token: permitdata?.token,
          });
          if (cancelled) return;
          if (listData.status !== 'OK') {
            message.error(listData.message || 'โหลดใบทะเบียนไม่สำเร็จ');
            return;
          }
          setLicenses(mapLicensesFromApi(listData.licenses));
        }
      } catch (error) {
        if (!cancelled) {
          message.error(error.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
        }
      } finally {
        if (!cancelled) {
          setBootLoading(false);
        }
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
  }, [isEdit, app_hostname, permitdata?.token]);

  useEffect(() => {
    const keyword = search.trim();
    if (!keyword) {
      setSearchSuggestions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await searchProductItems({
          appHostname: app_hostname,
          token: permitdata?.token,
          search: keyword,
        });
        if (!cancelled) {
          setSearchSuggestions(results);
        }
      } catch (error) {
        if (!cancelled) {
          message.error(error.message || 'ค้นหาสินค้าไม่สำเร็จ');
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, app_hostname, permitdata?.token]);

  const assignedProducts = useMemo(() => {
    const byId = new Map(assignedProductDetails.map((product) => [product.id, product]));
    return Object.keys(productAccess)
      .map((productId) => byId.get(productId))
      .filter(Boolean);
  }, [assignedProductDetails, productAccess]);

  const customCountByCategoryId = useMemo(
    () => countCustomAccessByCategory(assignedProducts),
    [assignedProducts],
  );

  const goBack = (refresh = false) => {
    navigate(LICENSE_LIST_PATH, refresh ? { state: { refresh: Date.now() } } : undefined);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    setSearchOpen(value.trim().length > 0);
  };

  const handleAddProduct = (product) => {
    setAssignedProductDetails((prev) => {
      if (prev.some((item) => item.id === product.id)) {
        return prev;
      }
      return [...prev, product];
    });
    setProductAccess((prev) => {
      if (isAssignedProduct(prev, product.id)) {
        return prev;
      }

      return {
        ...prev,
        [product.id]: 'none',
      };
    });
  };

  const handleRemoveProduct = (productId) => {
    setProductAccess((prev) => {
      if (!isAssignedProduct(prev, productId)) {
        return prev;
      }

      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const handleLicenseSelect = useCallback(
    async (licenseId) => {
      setSelectedLicenseId(licenseId || '');
      setSearch('');
      setSearchOpen(false);

      if (!licenseId) {
        setDescription('');
        setCategoryAccess({});
        setProductAccess({});
        setAssignedProductDetails([]);
        return;
      }

      try {
        const data = await fetchLicenseDetail({
          appHostname: app_hostname,
          token: permitdata?.token,
          licenseId,
        });
        if (data.status !== 'OK') {
          message.error(data.message || 'โหลดรายละเอียดไม่สำเร็จ');
          return;
        }
        setDescription(data.license?.license_desc || '');
        setCategoryAccess(data.category_access || {});
        setProductAccess(data.product_access || {});

        const extraProducts = (data.product_access_items || []).map((item) => ({
          id: item.productitem_id,
          name: item.productitem_name,
          code: item.productitem_id,
          inter: item.productitem_inter || '',
          barcode: '',
          categoryId: item.productcategory_id,
        }));
        if (extraProducts.length) {
          setAssignedProductDetails(extraProducts);
        } else {
          setAssignedProductDetails([]);
        }
      } catch (error) {
        message.error(error.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว');
      }
    },
    [app_hostname, permitdata?.token],
  );

  useEffect(() => {
    if (!isEdit || bootLoading) return;
    const licenseId = preselectLicenseIdRef.current;
    if (!licenseId) return;
    if (!licenses.some((license) => license.id === licenseId)) return;
    preselectLicenseIdRef.current = '';
    void handleLicenseSelect(licenseId);
  }, [isEdit, bootLoading, licenses, handleLicenseSelect]);

  const handleDelete = () => {
    if (!selectedLicenseId) {
      message.warning('กรุณาเลือกใบทะเบียนที่ต้องการลบ');
      return;
    }

    const license = licenses.find((item) => item.id === selectedLicenseId);
    const licenseName = license?.name || 'ใบทะเบียนนี้';

    Modal.confirm({
      title: 'ยืนยันการลบ',
      content: `คุณแน่ใจหรือไม่ที่จะลบ "${licenseName}"?`,
      okText: 'ลบ',
      okType: 'danger',
      cancelText: 'ยกเลิก',
      centered: true,
      onOk: async () => {
        try {
          setSaving(true);
          const data = await deleteLicense({
            appHostname: app_hostname,
            token: permitdata?.token,
            username: permitdata?.username,
            licenseId: selectedLicenseId,
          });
          if (data.status !== 'OK') {
            message.error(data.message || 'ไม่สามารถลบใบทะเบียนได้');
            return;
          }
          message.success(data.message || 'ลบใบทะเบียนสำเร็จ');
          goBack(true);
        } catch (error) {
          message.error(error.message || 'ไม่สามารถลบใบทะเบียนได้');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleSave = async () => {
    if (isEdit) {
      if (!selectedLicenseId) {
        message.warning('กรุณาเลือกใบทะเบียนที่ต้องการแก้ไข');
        return;
      }

      try {
        setSaving(true);
        const data = await editLicense({
          appHostname: app_hostname,
          token: permitdata?.token,
          username: permitdata?.username,
          licenseId: selectedLicenseId,
          description: description.trim(),
          categoryAccess: categoryAccessRef.current,
          productAccess: productAccessRef.current,
        });
        if (data.status !== 'OK') {
          message.error(data.message || 'ไม่สามารถบันทึกการแก้ไขใบทะเบียนได้');
          return;
        }
        message.success(data.message || 'บันทึกการแก้ไขใบทะเบียนสำเร็จ');
        goBack(true);
      } catch (error) {
        message.error(error.message || 'ไม่สามารถบันทึกการแก้ไขใบทะเบียนได้');
      } finally {
        setSaving(false);
      }
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      message.warning('กรุณากรอกชื่อใบทะเบียน');
      return;
    }

    try {
      setSaving(true);
      const data = await addLicense({
        appHostname: app_hostname,
        token: permitdata?.token,
        username: permitdata?.username,
        name: trimmedName,
        description: description.trim(),
        categoryAccess: categoryAccessRef.current,
        productAccess: productAccessRef.current,
      });
      if (data.status !== 'OK') {
        message.error(data.message || 'ไม่สามารถบันทึกใบทะเบียนได้');
        return;
      }
      message.success(data.message || 'บันทึกใบทะเบียนสำเร็จ');
      goBack(true);
    } catch (error) {
      message.error(error.message || 'ไม่สามารถบันทึกใบทะเบียนได้');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='page-wrapper license-form-page-wrapper'>
      <div className='page-card license-form-page'>
        <LicenseFormHeader pageTitle={pageTitle} onBack={goBack} />

        <div className='license-form-divider' />

        {bootLoading ? (
          <div className='license-form-empty license-form-empty--pending'>กำลังโหลดข้อมูล...</div>
        ) : (
          <>
            <LicenseFormFields
              isEdit={isEdit}
              name={name}
              onNameChange={setName}
              selectedLicenseId={selectedLicenseId}
              onLicenseSelect={handleLicenseSelect}
              licenses={licenses}
              description={description}
              onDescriptionChange={setDescription}
            />

            {showAccessSections ? (
              <>
                <div className='license-form-divider' />

                <LicenseCategoryAccessSection
                  categories={categories}
                  categoryAccess={categoryAccess}
                  customCountByCategoryId={customCountByCategoryId}
                  onAccessChange={(categoryId, value) =>
                    setCategoryAccess((prev) => ({ ...prev, [categoryId]: value }))
                  }
                />

                <div className='license-form-divider' />

                <LicenseProductAccessSection
                  search={search}
                  searchOpen={searchOpen}
                  onSearchChange={handleSearchChange}
                  onSearchOpenChange={setSearchOpen}
                  searchSuggestions={searchSuggestions}
                  categoryNameById={categoryNameById}
                  productAccess={productAccess}
                  assignedProducts={assignedProducts}
                  onAddProduct={handleAddProduct}
                  onRemoveProduct={handleRemoveProduct}
                  onAccessChange={(productId, value) =>
                    setProductAccess((prev) => ({ ...prev, [productId]: value }))
                  }
                />
              </>
            ) : (
              <div className='license-form-empty license-form-empty--pending'>
                กรุณาเลือกใบทะเบียนที่ต้องการแก้ไขสิทธิ์
              </div>
            )}
          </>
        )}

        <div className='license-form-actions'>
          {isEdit ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              className='license-form-btn-delete'
              onClick={handleDelete}
              disabled={bootLoading || saving || !selectedLicenseId}
            >
              ลบ
            </Button>
          ) : null}
          <Button className='license-form-btn-cancel' onClick={goBack} disabled={saving}>
            ยกเลิก
          </Button>
          <Button
            type='primary'
            className='license-form-btn-save'
            onClick={handleSave}
            loading={saving}
            disabled={bootLoading}
          >
            บันทึก
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ProductCategoryLicenseForm;
